import { Flex } from '@motili/ui';
import { styled } from 'styled-components';
import _ from 'lodash';
import {
    CSSProperties,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import {
    RemoveAllButton,
    ActiveFilterContainer,
} from 'common/components/data-grid/DataGridActiveFilters-V2';
import Config from 'Config';
import { WorkOrderPhotoTypeAPI } from 'common/stores/redux/MetaStore/MetaStoreApi.types';
import { Photo } from 'common/types/documentRequirements';
import DocumentRequirementsPhotoSearch from './DocumentRequirementsPhotoSearch';
import DocumentRequirementsPhotoFilterStatus from './DocumentRequirementsPhotoFilterStatus';
import DocumentRequirementsPhotoFilterMediaType from './DocumentRequirementsPhotoFilterMediaType';

export interface FilterItem {
    name: string;
    value: string;
}

export const enum FilterType {
    PHOTO_TYPE = 'PHOTO_TYPE',
    STATUS = 'STATUS',
    MEDIA_TYPE = 'MEDIA_TYPE',
}

export interface ActiveFilterItem {
    type: FilterType;
    display?: string;
    value?: string;
}

interface DocumentRequirementsPhotoSearchAndFilterProps {
    mediaList: Photo[];
    onSearchAndFilterChanged: (activeFilters: ActiveFilterItem[]) => void;
    style?: CSSProperties;
    disabled: boolean;
    isShowStatusFilter: boolean;
    isShowDocumentFilter: boolean;
}

const DocumentRequirementsPhotoSearchAndFilter = ({
    mediaList,
    onSearchAndFilterChanged,
    style,
    disabled = false,
    isShowStatusFilter = true,
    isShowDocumentFilter = false,
}: DocumentRequirementsPhotoSearchAndFilterProps) => {
    const searchRef = useRef(null);
    const mediaTypeRef = useRef(null);
    const statusRef = useRef(null);
    const [searchFilterList, setSearchFilterList] = useState<
        ActiveFilterItem[]
    >([]);
    const [statusFilter, setStatusFilter] = useState<FilterItem>();
    const [mediaTypeFilter, setMediaTypeFilter] = useState<FilterItem>();

    const handleSearchChanged = useCallback(
        (list: (WorkOrderPhotoTypeAPI | undefined)[] | undefined) => {
            setSearchFilterList(mapSearchFilter(list));
        },
        []
    );

    const handleStatusChanged = useCallback((item: FilterItem | undefined) => {
        setStatusFilter(item);
    }, []);

    const handleMediaTypeChanged = useCallback(
        (item: FilterItem | undefined) => {
            setMediaTypeFilter(item);
        },
        []
    );

    const filterList = useMemo(() => {
        const filters = [...searchFilterList];
        if (statusFilter) {
            filters.push(mapStatusFilter(statusFilter));
        }
        if (mediaTypeFilter) {
            filters.push(mapMediaTypeFilter(mediaTypeFilter));
        }
        return filters;
    }, [searchFilterList, statusFilter, mediaTypeFilter]);

    useEffect(() => {
        if (onSearchAndFilterChanged) {
            onSearchAndFilterChanged(filterList);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filterList]);

    const onFilterRemove = useCallback((type: FilterType, value?: string) => {
        switch (type) {
            case FilterType.PHOTO_TYPE:
                searchRef.current?.removeSelectedTypeList(value);
                setSearchFilterList([]);
                break;
            case FilterType.STATUS:
                statusRef.current?.clearSelectedStatus();
                setStatusFilter(undefined);
                break;
            case FilterType.MEDIA_TYPE:
                mediaTypeRef.current?.clearSelectedMediaType();
                setMediaTypeFilter(undefined);
                break;
            default:
                break;
        }
    }, []);
    const clearAllFilter = useCallback(() => {
        searchRef.current?.clearSelectedTypeList();
        mediaTypeRef.current?.clearSelectedMediaType();
        statusRef.current?.clearSelectedStatus();
        setSearchFilterList([]);
        setStatusFilter(undefined);
        setMediaTypeFilter(undefined);
    }, []);

    return (
        <div style={style}>
            <Flex fullWidth>
                <DocumentRequirementsPhotoSearch
                    ref={searchRef}
                    mediaList={mediaList}
                    onSearchChanged={handleSearchChanged}
                    disabled={disabled}
                />
                {isShowStatusFilter && (
                    <StyledDocumentRequirementsPhotoFilterStatus
                        ref={statusRef}
                        onSelectedStatusChanged={handleStatusChanged}
                        disabled={disabled}
                    />
                )}
                <StyledDocumentRequirementsPhotoFilterMediaType
                    ref={mediaTypeRef}
                    onSelectedMediaTypeChanged={handleMediaTypeChanged}
                    disabled={disabled}
                    isShowDocumentFilter={isShowDocumentFilter}
                />
            </Flex>
            {!_.isEmpty(filterList) && (
                <StyledTagAndRemoveContainer fullWidth>
                    <ActiveFilter
                        filters={filterList}
                        onFilterRemove={onFilterRemove}
                    />

                    <StyledRemoveAllContainer>
                        <RemoveAllButton onClearAll={clearAllFilter} />
                    </StyledRemoveAllContainer>
                </StyledTagAndRemoveContainer>
            )}
        </div>
    );
};
export default DocumentRequirementsPhotoSearchAndFilter;

interface ActiveFilterProps {
    filters: ActiveFilterItem[];
    onFilterRemove: (type: FilterType, value?: string) => void;
}

const ActiveFilter = ({ filters, onFilterRemove }: ActiveFilterProps) => {
    return (
        <ActiveFilterContainer>
            {_.map(filters, filter => (
                <StyledActiveFilterButton key={filter.display}>
                    {filter.display}
                    <i
                        style={{
                            fontSize: '19px',
                            color: Config.color.localGray,
                        }}
                        className='fa fa-times-circle'
                        onClick={() =>
                            onFilterRemove(filter.type, filter.value)
                        }
                        onKeyDown={e => {
                            if (e.key === 'Enter') {
                                onFilterRemove(filter.type, filter.value);
                            }
                        }}
                        tabIndex={0}
                        data-test-id='close-button'
                        role='button'
                    />
                </StyledActiveFilterButton>
            ))}
        </ActiveFilterContainer>
    );
};

const mapSearchFilter = (
    list: (WorkOrderPhotoTypeAPI | undefined)[] | undefined
) => {
    return _.map(
        list,
        value =>
            ({
                type: FilterType.PHOTO_TYPE,
                value: value?.id,
                display: value?.display,
            }) as ActiveFilterItem
    );
};

const mapStatusFilter = (item: FilterItem) => {
    return {
        type: FilterType.STATUS,
        display: `Status is ${item.name}`,
        value: item.value,
    } as ActiveFilterItem;
};

const mapMediaTypeFilter = (item: FilterItem) => {
    return {
        type: FilterType.MEDIA_TYPE,
        display: `Media Type is ${item.name}`,
        value: item.value,
    } as ActiveFilterItem;
};

const StyledRemoveAllContainer = styled(Flex)`
    width: 100px;
    justify-content: flex-end;
`;
const StyledTagAndRemoveContainer = styled(Flex)`
    margin-top: 16px;
    height: 30px;
    justify-content: space-between;
`;

const StyledActiveFilterButton = styled.div`
    background-color: ${Config.color.fadedGray};
    padding: 5px 8px;
    border-radius: 100px;
    display: flex;
    gap: 4px;
`;

const StyledDocumentRequirementsPhotoFilterMediaType = styled(
    DocumentRequirementsPhotoFilterMediaType
)`
    margin-left: 8px;
`;

const StyledDocumentRequirementsPhotoFilterStatus = styled(
    DocumentRequirementsPhotoFilterStatus
)`
    margin-left: 8px;
`;
